
import React, { useEffect, useState } from 'react';
import { adminService } from '../../services/admin.service';
import Badge from '../../components/common/Badge';

const AdminKyc: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchKyc = async () => {
    try {
      const res = await adminService.getKYCPending();
      setItems(Array.isArray(res) ? res : (res?.data || []));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  const handleApprove = async (userId: number) => {
    setProcessing(true);
    try {
      await adminService.approveKyc(userId);
      setSelectedUser(null);
      await fetchKyc();
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedUser || !rejectReason) return;
    setProcessing(true);
    try {
      await adminService.rejectKyc(selectedUser.userId, rejectReason);
      setRejecting(false);
      setSelectedUser(null);
      setRejectReason('');
      await fetchKyc();
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><div className="w-12 h-12 border-4 border-purple-600 border-t-transparent animate-spin rounded-full"></div></div>;

  return (
    <div className="space-y-10 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
        <div>
          <h2 className="text-4xl font-black text-white uppercase tracking-tighter">Identity Vault</h2>
          <p className="text-slate-500 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Biometric Audit Terminal</p>
        </div>
      </div>

      <div className="bg-slate-900/30 backdrop-blur-3xl rounded-[3rem] border border-white/5 overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/80 text-slate-600 uppercase text-[9px] tracking-[0.2em] font-black border-b border-white/5">
              <tr>
                <th className="px-10 py-8">Candidate Hash</th>
                <th className="px-10 py-8">Email Identifier</th>
                <th className="px-10 py-8">Audit Request</th>
                <th className="px-10 py-8">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.length > 0 ? items.map((u) => (
                <tr key={u.userId} className="hover:bg-purple-600/5 transition-colors group">
                  <td className="px-10 py-6">
                    <p className="text-white font-bold text-sm tracking-tight">{u.fname} {u.lname}</p>
                    <p className="text-[9px] text-slate-600 font-black uppercase mt-0.5">UID: {u.userId}</p>
                  </td>
                  <td className="px-10 py-6">
                    <p className="text-slate-400 font-mono text-xs">{u.email}</p>
                  </td>
                  <td className="px-10 py-6">
                    <Badge label="Pending Audit" variant="warning" />
                  </td>
                  <td className="px-10 py-6">
                    <button 
                      onClick={() => setSelectedUser(u)}
                      className="px-6 py-3 bg-purple-600 hover:bg-purple-500 text-white font-black text-[9px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-purple-600/20 active:scale-95"
                    >
                      Audit Record
                    </button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={4} className="px-10 py-32 text-center text-slate-500 uppercase text-[10px] font-black tracking-widest">
                    <div className="flex flex-col items-center opacity-30">
                       <svg className="w-16 h-16 mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                       Signal Queue Empty
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Unified Audit Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-xl animate-in fade-in duration-300">
           <div className="bg-slate-900 border border-white/5 w-full max-w-4xl max-h-[90vh] rounded-[3rem] shadow-2xl overflow-hidden flex flex-col relative">
              <div className="px-10 py-8 border-b border-white/5 flex items-center justify-between bg-slate-950/50">
                 <div>
                   <h3 className="text-2xl font-black text-white uppercase tracking-tight">Biometric Audit: {selectedUser.fname}</h3>
                   <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mt-1">Verify document clarity and authenticity</p>
                 </div>
                 <button onClick={() => {setSelectedUser(null); setRejecting(false);}} className="text-slate-500 hover:text-white transition-colors p-3 bg-slate-950/40 rounded-2xl">
                   <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                 </button>
              </div>

              <div className="flex-1 overflow-y-auto p-10 custom-scrollbar flex flex-col items-center bg-slate-950/20">
                 <img src={selectedUser.document} alt="KYC Document" className="max-w-full rounded-3xl shadow-2xl border border-white/5 object-contain" />
              </div>

              <div className="px-10 py-8 border-t border-white/5 bg-slate-950/50">
                 {rejecting ? (
                   <div className="space-y-6 animate-in slide-in-from-bottom-4">
                      <textarea 
                        autoFocus
                        value={rejectReason}
                        onChange={e => setRejectReason(e.target.value)}
                        className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-6 text-white font-bold outline-none focus:ring-2 focus:ring-rose-500/40 h-24 text-sm placeholder-slate-800"
                        placeholder="Define rejection protocol (e.g. Low Resolution, Expired ID)..."
                      />
                      <div className="flex space-x-4">
                        <button onClick={() => setRejecting(false)} className="flex-1 py-5 bg-slate-800 text-slate-400 font-black uppercase text-[10px] tracking-widest rounded-2xl">Abort Rejection</button>
                        <button onClick={handleReject} disabled={!rejectReason || processing} className="flex-2 py-5 bg-rose-600 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-xl shadow-rose-600/30">Confirm Rejection</button>
                      </div>
                   </div>
                 ) : (
                   <div className="flex space-x-4">
                      <button onClick={() => setRejecting(true)} className="flex-1 py-5 bg-rose-600/10 hover:bg-rose-600 text-rose-500 hover:text-white border border-rose-500/20 rounded-2xl font-black uppercase text-[10px] tracking-widest transition-all">Deny Access</button>
                      <button onClick={() => handleApprove(selectedUser.userId)} disabled={processing} className="flex-2 py-5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-emerald-600/30 transition-all">Approve Identity</button>
                   </div>
                 )}
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminKyc;
